import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Plus, Pencil, Trash2, CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { Badge } from '../ui/badge';
import ConfirmModal from '../ui/ConfirmModal';
import { Tooltip } from '../ui/tooltip';
import MaintenanceScheduleDialog from './MaintenanceScheduleDialog';
import MarkPerformedDialog from './MarkPerformedDialog';
import BulkApplySchedulesDialog from './BulkApplySchedulesDialog';
import {
  SCHEDULE_STATUS_BADGE,
  formatInterval,
  formatLastPerformed,
  formatNextDue,
  formatRemaining,
  scheduleStatus,
  triggerLabel,
} from '../../lib/maintenanceTriggers';
import type { AssetKind, MaintenanceSchedule } from '../../types/equipment';

/** Stable empty reference so the counts memo does not re-run every render. */
const NO_SCHEDULES: MaintenanceSchedule[] = [];

interface MaintenanceSchedulesProps {
  equipmentId: string;
  /** Drives the trigger picker — read from the equipment row's asset_kind. */
  assetKind: AssetKind | string | null | undefined;
}

export function MaintenanceSchedules({ equipmentId, assetKind }: MaintenanceSchedulesProps) {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<MaintenanceSchedule | null>(null);
  const [marking, setMarking] = useState<MaintenanceSchedule | null>(null);
  const [deleting, setDeleting] = useState<MaintenanceSchedule | null>(null);
  const [bulkTemplate, setBulkTemplate] = useState<MaintenanceSchedule | null>(null);
  const [showBulk, setShowBulk] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-maintenance-schedules', equipmentId],
    queryFn: () =>
      api.equipmentMaintenanceSchedules.list(equipmentId).then((r) => {
        const d = r.data?.data ?? r.data;
        return Array.isArray(d) ? (d as MaintenanceSchedule[]) : [];
      }),
    enabled: !!equipmentId,
  });

  const schedules: MaintenanceSchedule[] = data ?? NO_SCHEDULES;

  const counts = useMemo(() => {
    const tally = { overdue: 0, due_soon: 0 };
    schedules.forEach((s) => {
      const status = scheduleStatus(s);
      if (status === 'overdue') tally.overdue += 1;
      if (status === 'due_soon') tally.due_soon += 1;
    });
    return tally;
  }, [schedules]);

  const deleteMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      api.equipmentMaintenanceSchedules.delete(equipmentId, scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-schedules', equipmentId] });
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-due'] });
      toast.success('Schedule deleted');
      setDeleting(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to delete schedule');
    },
  });

  const openBulk = (template: MaintenanceSchedule | null) => {
    setBulkTemplate(template);
    setShowBulk(true);
  };

  return (
    <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border overflow-hidden">
      {/* Section header — actions right-aligned, matching the Maintenance History card */}
      <div className="p-4 border-b border-fieldiq-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-fieldiq-accent" />
          <div>
            <h3 className="text-base font-bold text-white">Maintenance Schedules</h3>
            <p className="text-xs text-fieldiq-text mt-0.5">
              {schedules.length} schedule{schedules.length !== 1 ? 's' : ''}
              {counts.overdue > 0 && (
                <> · <span className="text-red-400 font-semibold">{counts.overdue} overdue</span></>
              )}
              {counts.due_soon > 0 && (
                <> · <span className="text-amber-400 font-semibold">{counts.due_soon} due soon</span></>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openBulk(null)}
            className="flex items-center gap-1.5 bg-fieldiq-dark border border-fieldiq-border text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:border-fieldiq-accent transition-colors"
          >
            <Copy size={14} /> Apply to Multiple Units
          </button>
          <button
            onClick={() => { setEditing(null); setShowDialog(true); }}
            className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-400 transition-colors"
          >
            <Plus size={14} /> Add Schedule
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-fieldiq-accent" />
        </div>
      ) : schedules.length === 0 ? (
        <div className="py-8 text-center text-fieldiq-muted text-sm px-4">
          No maintenance schedules yet. Click "Add Schedule" to set up preventive maintenance for
          this unit.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-fieldiq-dark text-fieldiq-muted text-xs uppercase">
                <th className="text-left px-4 py-2.5 font-medium">Name</th>
                <th className="text-left px-4 py-2.5 font-medium">Trigger</th>
                <th className="text-left px-4 py-2.5 font-medium">Interval</th>
                <th className="text-left px-4 py-2.5 font-medium">Last Performed</th>
                <th className="text-left px-4 py-2.5 font-medium">Next Due</th>
                <th className="text-center px-4 py-2.5 font-medium">Status</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule) => {
                const status = scheduleStatus(schedule);
                const chip = SCHEDULE_STATUS_BADGE[status];
                const remaining = formatRemaining(schedule);
                // The server explains itself — surface it as the chip's tooltip.
                const explanation =
                  schedule.evaluation?.error || schedule.evaluation?.basis || undefined;

                return (
                  <tr
                    key={schedule.id}
                    className="border-t border-fieldiq-border hover:bg-fieldiq-dark/50 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-white">
                      {schedule.name}
                      {schedule.isActive === false && (
                        <span className="ml-2 text-[10px] font-bold uppercase text-fieldiq-muted">
                          Paused
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">
                      {triggerLabel(schedule.triggerType)}
                    </td>
                    <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">
                      {formatInterval(schedule)}
                    </td>
                    <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">
                      {formatLastPerformed(schedule)}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="text-white">{formatNextDue(schedule)}</span>
                      {remaining && (
                        <span className="block text-[11px] text-fieldiq-muted">{remaining}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge
                        variant="outline"
                        className={`${chip.cls} whitespace-nowrap`}
                        title={explanation}
                      >
                        {chip.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      {/* align="end" keeps the bubbles inside the table's
                          overflow-x-auto wrapper instead of off its right edge. */}
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip content="Mark performed" align="end">
                          <button
                            onClick={() => setMarking(schedule)}
                            className="p-1.5 rounded hover:bg-fieldiq-dark text-fieldiq-muted hover:text-emerald-400 transition-colors"
                            aria-label="Mark performed"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Apply this schedule to other units" align="end">
                          <button
                            onClick={() => openBulk(schedule)}
                            className="p-1.5 rounded hover:bg-fieldiq-dark text-fieldiq-muted hover:text-white transition-colors"
                            aria-label="Apply this schedule to other units"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Edit" align="end">
                          <button
                            onClick={() => { setEditing(schedule); setShowDialog(true); }}
                            className="p-1.5 rounded hover:bg-fieldiq-dark text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                            aria-label="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete" align="end">
                          <button
                            onClick={() => setDeleting(schedule)}
                            className="p-1.5 rounded hover:bg-red-500/10 text-fieldiq-muted hover:text-red-400 transition-colors"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <MaintenanceScheduleDialog
        open={showDialog}
        onClose={() => { setShowDialog(false); setEditing(null); }}
        equipmentId={equipmentId}
        assetKind={assetKind}
        schedule={editing}
      />

      <MarkPerformedDialog
        open={!!marking}
        onClose={() => setMarking(null)}
        equipmentId={equipmentId}
        schedule={marking}
      />

      <BulkApplySchedulesDialog
        open={showBulk}
        onClose={() => { setShowBulk(false); setBulkTemplate(null); }}
        equipmentId={equipmentId}
        assetKind={assetKind}
        template={bulkTemplate}
      />

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete Maintenance Schedule"
        body={
          <p>
            Delete "<span className="text-white font-medium">{deleting?.name}</span>"? Past
            maintenance records are kept — only the recurring schedule is removed.
          </p>
        }
        confirmLabel={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

export default MaintenanceSchedules;
